import {onWindowMessage} from '../../@shared/ts/dom';
import {type InitMessage} from '../../@type/window-messages';
import {GLOBAL} from './deprecated/global';
import {initMerchantAccount} from './features/account';
import {initAdditionalFields} from './features/additionalFields';
import {initAddressAutocomplete} from './features/addressAutocomplete';
import {initShippingForm} from './features/billingShippingFields';
import {initCart} from './features/cart';
import {initCouponInput} from './features/coupon';
import {initCurrency} from './features/currency';
import {initCurrencySwitcher} from './features/currencySwitch';
import {initCustomer, installCustomerBillingFields, installCustomerFormFields, loadCustomer} from './features/customer';
import {initCustomOrderMessaging} from './features/customOrderMessaging';
import {initGiftCardInput} from './features/giftCard';
import {initLanguage} from './features/language';
import {initMerchantLogo} from './features/merchantLogo';
import {initMetrics} from './features/metrics';
import {initModal} from './features/modal';
import {initOneClickUpsell} from './features/oneClickUpsell';
import {initDeliveryDate} from './features/orderDelivery';
import {initPaymentGatewaySelector} from './features/paymentSelector';
import {initRelatedProducts} from './features/relatedProducts';
import {initShipping} from './features/shipping';
import {initSummary} from './features/summary';
import {initVAT} from './features/vat';
import {initBotProtection} from './features/botProtection';
import {getOrderService, type OrderService} from './payment/order';
import {initPeachPayMethods} from './payment/peachpay/peachpay';
import {loadSession} from './payment/session';
import {FeatureFlag} from '../../@type/features';
import {Feature, setFeatureSupport, stopModalLoading, updateEnvironment} from './reducers/environmentReducer';
import {updateMerchantHostName, updateMerchantId, updateMerchantName} from './reducers/merchantConfigurationReducer';
import {store} from './store';
import {determinePageType} from './util/environment';
import {captureSentryException, initSentry} from '../../@shared/ts/sentry';
import {PLUGIN_VERSION} from '../../git';

(window as any).store = store;

initSentry(`peachpay-checkout@${PLUGIN_VERSION}`, 'https://39b5a2e17e264bb5a6ea5abe9bc6cf61@o470066.ingest.sentry.io/5660513');

/**
 * Entry point for the peachpay checkout.
 */
(function () {
	/**
	 * Inits data from the php side.
	 */
	onWindowMessage<InitMessage>('init', async message => {
		GLOBAL.phpData = message.phpData;

		// We first need to initialize the language for use in the form fields
		store.dispatch(setFeatureSupport(message.phpData.feature_support));
		initLanguage(message);

		// #region Customer form fields Init
		// We then need to set the merchant hostname because addFormFields depends on it.
		store.dispatch(updateMerchantHostName(message.merchantHostname));
		// It is important this function runs second because event listeners depend on the HTML it adds.
		installCustomerFormFields(message);
		installCustomerBillingFields(message);
		// #endregion

		loadSession();

		store.dispatch(updateMerchantHostName(message.merchantHostname));
		store.dispatch(updateMerchantName(message.phpData.merchant_name));
		store.dispatch(updateMerchantId(message.phpData.merchant_id));
		store.dispatch(updateEnvironment({
			pluginIsTestMode: Boolean(message.isTestMode),
			pluginPageType: determinePageType(message.isCartPage, message.isCheckoutPage, message.isShopPage),
			customerIsMobile: message.isMobile,
			pluginButtonColor: message.phpData.button_color,
			pluginVersion: message.phpData.version,
		}));

		/**
		 * Feature callbacks to perform a action or insert data into the redux store once the data is available.
		 */
		initOneClickUpsell();
		initDeliveryDate();
		initMetrics();
		initCart();
		initBotProtection();
		initSummary(message);
		initCouponInput(message);
		initGiftCardInput();
		initShipping(message);
		initShippingForm();
		initCustomer(message);
		initCurrency(message);
		initMerchantAccount(message);
		initVAT(message);
		initRelatedProducts();
		initAdditionalFields(message);
		initCurrencySwitcher();
		initPaymentGatewaySelector();
		initModal(message);
		initAddressAutocomplete(message);
		initCustomOrderMessaging();
		initMerchantLogo();

		// Payment Processor support
		const orderService = getOrderService();

		initPeachPayMethods(orderService);

		const initPaymentIntegrationResponse = await Promise.allSettled([
			importPaymentIntegration(importGateway.authnet, orderService),
			importPaymentIntegration(importGateway.stripe, orderService),
			importPaymentIntegration(importGateway.square, orderService),
			importPaymentIntegration(importGateway.paypal, orderService),
			importPaymentIntegration(importGateway.amazon, orderService),
			importPaymentIntegration(importGateway.poynt, orderService),
		]);

		initPaymentIntegrationResponse.forEach(response => {
			if (response.status === 'fulfilled') {
				return;
			}

			console.error(response.reason);
		});

		await loadCustomer();
		window.dispatchEvent(new CustomEvent('pp-update-afterpay-branding'));

		if (!store.getState().peachPayCustomer.preferred_payment_method) {
			store.dispatch(updateEnvironment({modalPageType: 'info'}));
		}

		store.dispatch(stopModalLoading());

		window.parent?.postMessage('pp-loaded', location.origin);
	});

	onWindowMessage('captureInfiniteLoadingError', async (event: any) => {
		captureSentryException(new Error('pp-button stuck loading infinitely'), {
			...event.data,
			checkout_window_state: JSON.stringify(store.getState()),
			peachpay_data: JSON.stringify(GLOBAL.phpData),
		});
	});
})();

type GatewayIntegrationImport = {
	featureFlag: FeatureFlag;
	import_: () => Promise<{default: (orderService: OrderService) => Promise<void> | void}>;
};

const importGateway = {
	authnet: {
		featureFlag: FeatureFlag.AUTHNET_GATEWAY,
		import_: async () => import('./payment/authnet/authnet'),
	},
	stripe: {
		featureFlag: FeatureFlag.STRIPE_GATEWAYS,
		import_: async () => import('./payment/stripe/stripe'),
	},
	square: {
		featureFlag: FeatureFlag.SQUARE_GATEWAYS,
		import_: async () => import('./payment/square/square'),
	},
	paypal: {
		featureFlag: FeatureFlag.PAYPAL_GATEWAYS,
		import_: async () => import('./payment/paypal/paypal'),
	},
	amazon: {
		featureFlag: FeatureFlag.AMAZON_GATEWAY,
		import_: async () => import('./payment/amazon/amazon'),
	},
	poynt: {
		featureFlag: FeatureFlag.POYNT_GATEWAYS,
		import_: async () => import('./payment/poynt/poynt'),
	},
} satisfies Record<string, GatewayIntegrationImport>;

/**
 * Import payment dynamically based on given information.
 *
 * @param importGateway
 * @param orderService
 */
async function importPaymentIntegration(importGateway: GatewayIntegrationImport, orderService: OrderService) {
	if (!Feature.enabled(importGateway.featureFlag)) {
		return null;
	}

	return new Promise((resolve, reject) => {
		importGateway.import_()
			.then(gateway => {
				resolve(gateway.default(orderService));
			})
			.catch(e => {
				console.error(`Failed to import payment integration: ${importGateway.featureFlag}`, e);
				reject(e);
			});
	});
}
